<template>
    <div class="section-container w-100">
        <SectionTitle :title="`${title} Capacity`">
            <MoreInfo :id="`${type}-capacity-more-info`" :title="`${title} Capacity`">
                <p>
                    Percentage of match average {{ title }} capacity
                    reached, by speed bins.
                </p>
            </MoreInfo>
        </SectionTitle>

        <MissingDataText :message="`${title} capacity unavailable`" v-if="!hasData" />

        <div class="overflow-auto" style="max-height: 50vh;" v-else>
            <table>
                <tr v-for="(row, index) in rows" :key="'row-' + index" style="border-bottom: 1px solid grey;">
                    <td class="label-cell">{{ row.label }}</td>
                    <td v-for="(bin, i) in speedBins" :key="'cell-' + i">
                        <div class="battery" :style="{ backgroundColor: getColour(row.values[bin]) }">
                            <span>{{ row.values[bin] || 0 }}%</span>
                        </div>
                    </td>
                </tr>
                <tr class="sticky-speed-row">
                    <td class="label-cell">
                        Speed Bins (<MeasurementUnit :type="Speed" />)
                    </td>
                    <td v-for="(bin, index) in speedBins" :key="'speed-' + index">
                        <div style="border-top: 1vh solid white; width: 2px; margin: auto;"></div>
                        <div class="battery">
                            <span>{{ formatBin(bin) }}</span>
                        </div>
                    </td>
                </tr>
            </table>
        </div>
    </div>
</template>

<script>
import SectionTitle from "@/components/SectionTitle.vue";
import MissingDataText from "@/components/MissingDataText.vue";
import MeasurementUnit from "@/components/MeasurementUnit.vue";
import MoreInfo from "@/components/MoreInfo.vue";
import { MeasurementTypes, getMeasurementUnit } from "@/utils/MeasurementSystem";

export default {
    props: {
        data: [Object, Array],
        mode: {
            type: String,
            default: "single",
            validator: val => ["single", "multi"].includes(val)
        },
        type: {
            type: String,
            required: true
        },
        title: String
    },
    components: {
        SectionTitle,
        MissingDataText,
        MeasurementUnit,
        MoreInfo
    },
    data() {
        return {
            Speed: MeasurementTypes.Speed,
            speedUnit: getMeasurementUnit(MeasurementTypes.Speed),
            rows: [],
            speedBins: [],
            hasData: false
        };
    },
    methods: {
        getColour(value) {
            const numeric = value === "120+" ? 120 : parseInt(value, 10);
            if (isNaN(numeric)) return "rgba(26, 213, 237, 1)";
            const opacity = (((numeric * 0.8) + 10) * 0.01).toFixed(2);
            return `rgba(26, 213, 237, ${opacity})`;
        },
        formatBin(range) {
            const [start, end] = range.split("-").map(Number);
            return `${this.speedUnit.formatStandard(start)}-${this.speedUnit.formatStandard(end)}`;
        },
        processData() {
            if (this.mode === 'single') {
                this.hasData = this.data != null;
            } else {
                this.hasData = this.data.length > 0 && this.data.map(session => {
                    const typeData = session.accelDecelCapacity[this.type];
                    return (typeData && Object.keys(typeData).length > 0);
                });
            }

            if (this.hasData) {
                const isSingleMode = this.mode === "single";
                const sessions = isSingleMode ? [this.data] : this.data;

                // Collect all unique speed bins across sessions
                const allBins = new Set();
                sessions.forEach(session => {
                    const base = isSingleMode ? session : session.accelDecelCapacity;
                    const accelLabels = base?.accelPercentageLabels || {};
                    const decelLabels = base?.decelPercentageLabels || {};
                    Object.keys({ ...accelLabels, ...decelLabels }).forEach(bin => allBins.add(bin));
                });

                // Reconstruct bins from 3m/s upward in 0.5m/s steps
                const binCount = allBins.size;
                this.speedBins = Array.from({ length: binCount }, (_, i) => {
                    const start = 3 + i * 0.5;
                    return `${start}-${start + 0.5}`;
                });

                if (isSingleMode) {
                    this.rows = [
                        {
                            label: "Acceleration",
                            values: this.speedBins.reduce((acc, bin) => {
                                acc[bin] = this.data.accelPercentageLabels?.[bin] || 0;
                                return acc;
                            }, {})
                        },
                        {
                            label: "Deceleration",
                            values: this.speedBins.reduce((acc, bin) => {
                                acc[bin] = this.data.decelPercentageLabels?.[bin] || 0;
                                return acc;
                            }, {})
                        }
                    ];
                } else {
                    const sourceType = this.type === "accelPercentageLabels" ? "accelPercentageLabels" : "decelPercentageLabels";
                    this.rows = this.data.slice() // create a shallow copy to avoid mutating the original data
                        .sort((a, b) => new Date(b.sessionDate) - new Date(a.sessionDate)).map(session => {
                        const labels = session.accelDecelCapacity?.[sourceType] || {};
                        return {
                            label: session.sessionDate,
                            values: this.speedBins.reduce((acc, bin) => {
                                acc[bin] = labels[bin] || 0;
                                return acc;
                            }, {})
                        };
                    });
                }
            }
        }
    },
    watch: {
        data: {
            handler() {
                this.processData();
            },
            immediate: true,
            deep: true
        }
    }
};
</script>

<style scoped>
table {
    margin: auto;  
    white-space: nowrap;
}
td {
    padding: 0.1em 0.2em;
}
.label-cell {
    border-right: 1px solid grey;
    padding-right: 1vw;
    white-space: nowrap;
}
.battery {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: auto;
    min-width: 5vw;
    height: 5vh;
}
.sticky-speed-row {
    position: sticky;
    bottom: 0;
    background: var(--bg-primary);
    z-index: 1;
}
</style>